import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import FactionSpineCanvas from "../spine/FactionSpineCanvas";
import type { FactionHeroSlide, WorldFactionId } from "./worldFactionContent";
import { useCarouselPointerDrag } from "./ImageCarousel";
import { preventContextMenuOnDesktop } from "./landingDomGuards";
import infernalFallback from "../../assets/img/landing/infernal_1.png";
import celestialFallback from "../../assets/img/landing/celestial_1.png";
import ephemeralFallback from "../../assets/img/landing/ephemeral_1.png";
import factionlessFallback from "../../assets/img/landing/factionless_1.webp";

const HERO_FADE_OUT_MS = 300;
const HERO_FADE_IN_MS = 300;
const HERO_FADE_IN_MAX_WAIT_MS = 2500;

function fallbackForFaction(f: WorldFactionId): string {
  switch (f) {
    case "infernal":
      return infernalFallback;
    case "celestial":
      return celestialFallback;
    case "ephemeral":
      return ephemeralFallback;
    case "factionless":
      return factionlessFallback;
    default:
      return infernalFallback;
  }
}

export type FactionHeroCarouselProps = {
  faction: WorldFactionId;
  heroes: FactionHeroSlide[];
  /** When false, autoplay is paused (faction slide not visible). */
  active: boolean;
  intervalMs: number;
  ariaLabel: string;
};

/**
 * Spine hero carousel for one faction. Pointer-drag and autoplay when `active`.
 */
const FactionHeroCarousel = ({
  faction,
  heroes,
  active,
  intervalMs,
  ariaLabel,
}: FactionHeroCarouselProps): ReactElement => {
  const [index, setIndex] = useState(0);
  /** Start at 0 so cold load / refresh never shows an empty WebGL canvas at full opacity (avoids compositor flash). */
  const [heroShellOpacity, setHeroShellOpacity] = useState(0);
  /** When true, shell opacity changes skip CSS transition (snap after swap / reveal under cover). */
  const [shellSkipOpacityTransition, setShellSkipOpacityTransition] =
    useState(false);
  /** Opaque cover fades in over the hero during hand-off so the WebGL canvas is never under an animating opacity ancestor (fixes outgoing-hero fade flashes). */
  const [heroCoverOpacity, setHeroCoverOpacity] = useState(0);
  const [spineFailed, setSpineFailed] = useState<boolean[]>(() =>
    heroes.map(() => false)
  );
  const total = heroes.length;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const heroNavBusyRef = useRef(false);
  const pendingHeroFadeInRef = useRef(false);
  /** First paint after faction becomes active or data resets — wait for Spine before fading shell in. */
  const pendingInitialShellRevealRef = useRef(false);
  const heroTimerRefs = useRef<{
    fadeOutDone?: number;
    fadeInDone?: number;
    fadeInMaxWait?: number;
  }>({});

  const clearHeroNavTimers = useCallback(() => {
    const { fadeOutDone, fadeInDone, fadeInMaxWait } = heroTimerRefs.current;
    if (fadeOutDone !== undefined) window.clearTimeout(fadeOutDone);
    if (fadeInDone !== undefined) window.clearTimeout(fadeInDone);
    if (fadeInMaxWait !== undefined) window.clearTimeout(fadeInMaxWait);
    heroTimerRefs.current = {};
    pendingHeroFadeInRef.current = false;
    setHeroCoverOpacity(0);
  }, []);

  useEffect(() => {
    clearHeroNavTimers();
    heroNavBusyRef.current = false;
    setIndex(0);
    setSpineFailed(heroes.map(() => false));
  }, [clearHeroNavTimers, faction, heroes]);

  /** When this strip becomes visible or hero data changes, hide shell until Spine first draw (fixes refresh flash). */
  useEffect(() => {
    if (!active) return;
    pendingInitialShellRevealRef.current = true;
    setHeroShellOpacity(0);
    setHeroCoverOpacity(0);
  }, [active, faction, heroes]);

  const revealHeroShellAfterSpine = useCallback(() => {
    const hadFadeIn = pendingHeroFadeInRef.current;
    const hadInitial = pendingInitialShellRevealRef.current;
    if (!hadFadeIn && !hadInitial) return;
    pendingHeroFadeInRef.current = false;
    pendingInitialShellRevealRef.current = false;
    const maxW = heroTimerRefs.current.fadeInMaxWait;
    if (maxW !== undefined) {
      window.clearTimeout(maxW);
      delete heroTimerRefs.current.fadeInMaxWait;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (hadFadeIn) {
          setShellSkipOpacityTransition(true);
          setHeroShellOpacity(1);
          setHeroCoverOpacity(0);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setShellSkipOpacityTransition(false);
            });
          });
        } else {
          setHeroShellOpacity(1);
        }
        if (heroTimerRefs.current.fadeInDone !== undefined) {
          window.clearTimeout(heroTimerRefs.current.fadeInDone);
        }
        if (heroNavBusyRef.current && hadFadeIn) {
          heroTimerRefs.current.fadeInDone = window.setTimeout(() => {
            heroNavBusyRef.current = false;
            delete heroTimerRefs.current.fadeInDone;
          }, HERO_FADE_IN_MS);
        }
      });
    });
  }, []);

  const markSpineFailed = useCallback(
    (i: number) => {
      setSpineFailed((prev) => {
        if (prev[i]) return prev;
        const next = [...prev];
        next[i] = true;
        return next;
      });
      revealHeroShellAfterSpine();
    },
    [revealHeroShellAfterSpine]
  );

  const advanceHero = useCallback(
    (dir: number) => {
      if (total < 2 || heroNavBusyRef.current) return;
      heroNavBusyRef.current = true;
      clearHeroNavTimers();

      const prefersReduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const outMs = prefersReduced ? 0 : HERO_FADE_OUT_MS;
      const inMs = prefersReduced ? 0 : HERO_FADE_IN_MS;

      // Fade a solid cover over the hero — not the shell that wraps WebGL — to avoid compositor flashes on some heroes during fade-out.
      setHeroCoverOpacity(1);

      heroTimerRefs.current.fadeOutDone = window.setTimeout(() => {
        setIndex((i) => (i + dir + total) % total);
        if (prefersReduced || inMs === 0) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              pendingHeroFadeInRef.current = false;
              pendingInitialShellRevealRef.current = false;
              setShellSkipOpacityTransition(true);
              setHeroShellOpacity(1);
              setHeroCoverOpacity(0);
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setShellSkipOpacityTransition(false);
                });
              });
              heroNavBusyRef.current = false;
            });
          });
          delete heroTimerRefs.current.fadeOutDone;
          return;
        }
        setShellSkipOpacityTransition(true);
        setHeroShellOpacity(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setShellSkipOpacityTransition(false);
          });
        });
        pendingHeroFadeInRef.current = true;
        heroTimerRefs.current.fadeInMaxWait = window.setTimeout(() => {
          revealHeroShellAfterSpine();
        }, HERO_FADE_IN_MAX_WAIT_MS);
        delete heroTimerRefs.current.fadeOutDone;
      }, outMs);
    },
    [clearHeroNavTimers, revealHeroShellAfterSpine, total]
  );

  useEffect(() => {
    return () => clearHeroNavTimers();
  }, [clearHeroNavTimers]);

  useEffect(() => {
    if (!active || total < 2) return;
    const t = window.setInterval(() => advanceHero(1), intervalMs);
    return () => window.clearInterval(t);
  }, [active, advanceHero, intervalMs, total]);

  useCarouselPointerDrag(wrapRef, advanceHero);

  const onSpineFirstDraw = useCallback(() => {
    revealHeroShellAfterSpine();
  }, [revealHeroShellAfterSpine]);

  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      revealHeroShellAfterSpine();
    }, HERO_FADE_IN_MAX_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [active, faction, heroes, revealHeroShellAfterSpine]);

  const fallback = fallbackForFaction(faction);

  return (
    <>
      <div
        className="carousel-wrap carousel-wrap-inner carousel-wrap-grabbable"
        ref={wrapRef}
        role="region"
        aria-label={ariaLabel}
      >
        <div
          className="faction-hero-fade-shell"
          style={{
            opacity: heroShellOpacity,
            transition: shellSkipOpacityTransition ? "none" : undefined,
          }}
        >
          <div className="carousel-track carousel-track-inner">
            {heroes.map((s, i) => {
              const showSpine =
                active && i === index && !spineFailed[i];
              const showFallback = active && i === index && !showSpine;
              return (
                <div
                  key={`${s.jsonFile}-${i}`}
                  className={`carousel-slide carousel-slide-world${i === index ? " is-active" : ""}`}
                >
                  <div className="carousel-slide-visual">
                    {showFallback ? (
                      <img
                        className="carousel-slide-fallback"
                        src={fallback}
                        alt={s.alt}
                        loading="eager"
                        decoding="async"
                        onContextMenu={preventContextMenuOnDesktop}
                      />
                    ) : null}
                    {showSpine ? (
                      <FactionSpineCanvas
                        key={`${s.jsonFile}-${s.atlasFile}`}
                        active
                        jsonFile={s.jsonFile}
                        atlasFile={s.atlasFile}
                        onLoadFailed={() => markSpineFailed(i)}
                        onReady={onSpineFirstDraw}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div
          className="faction-hero-transition-cover"
          aria-hidden
          style={{ opacity: heroCoverOpacity }}
        />
      </div>
    </>
  );
};

export default FactionHeroCarousel;
